import React from 'react';

import styled from 'styled-components';

import { Modal } from '../../Modal';
import { useLayoutStrategy } from '../contexts/LayoutStrategyContext';
import { LayoutArea } from '../types';

const ModalBodyContainer = styled.div`
  min-height: 200px;
  max-height: 80vh;
  overflow: auto;
  padding: 1rem;
`;

const FallbackMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.colors?.palette?.grayscale?.[4] ?? '#666'};
`;

/**
 * Smart modal that automatically shows content when panels can't fit in viewport.
 * Uses the existing Modal component for consistent styling and behavior.
 */
export const SmartModal: React.FC = () => {
  const { activeModal, hideModal } = useLayoutStrategy();

  if (!activeModal) return null;

  // Determine modal size class based on the size property
  const getModalSize = (size?: 'small' | 'medium' | 'large' | 'fullscreen') => {
    switch (size) {
      case 'small':
        return 'sm';
      case 'large':
        return 'lg';
      case 'fullscreen':
        return 'xl';
      default:
        return undefined; // medium is default
    }
  };

  return (
    <Modal
      show={true}
      onHide={hideModal}
      title={activeModal.title ?? `${activeModal.area} Content`}
      size={getModalSize(activeModal.size)}
      backdrop={true}
      keyboard={true}
      centered
    >
      <ModalBodyContainer>
        {activeModal.content ? (
          activeModal.content
        ) : (
          <FallbackMessage>
            <h4>Content Not Available</h4>
            <p>This content couldn&apos;t be displayed in the current viewport size. Try expanding your window or switching to a larger screen.</p>
          </FallbackMessage>
        )}
      </ModalBodyContainer>
    </Modal>
  );
};

/**
 * Hook for components to register themselves for smart modal display
 * when they can't fit in the current layout.
 */
export const useSmartModalFallback = (area: string, content: React.ReactNode, title?: string) => {
  const { strategy, showModal } = useLayoutStrategy();

  const showAsModal = React.useCallback(() => {
    showModal({
      area: area as LayoutArea,
      content,
      title: title ?? `${area} Content`,
      size: area === 'CONTENT' ? 'fullscreen' : 'large',
    });
  }, [area, content, title, showModal]);

  return {
    canShowInline: strategy.canShowInline(area as LayoutArea),
    showAsModal,
    isInModal: strategy.shouldUseModal(area as LayoutArea),
  };
};
